import type { RunState } from '~/types/game'
import { weeklyDateKey } from '~/utils/seededRandom'
import { starweaveProgressSignature } from '~/utils/starweavePersistence'
import { ACCOUNT_DELETION_SESSION } from '~/utils/accountDeletion'
import { onAppStateChange } from '~/composables/useAppLifecycle'

/**
 * Loads a saved run on startup and auto-saves changes so a page refresh
 * resumes the run.
 *
 * ⚡ Throttled since v1.47 (review finding H1): the encounter clock
 * mutates mission timers 10×/second, and the old "save on every change"
 * watcher serialized the ENTIRE run (50–200KB) to localStorage on each
 * tick — a constant main-thread tax for the whole fight. Now:
 *
 * - STRUCTURAL changes (phase, dispatches, resolutions, gold, roster,
 *   rewards…) are detected by a cheap field signature and saved
 *   immediately — nothing a player would call "progress" ever waits;
 * - pure timer churn marks the run dirty and lands in ONE trailing
 *   write per SAVE_EVERY_MS;
 * - pagehide / tab-hide force a flush, so closing or backgrounding the
 *   tab mid-tick loses nothing.
 *
 * Worst case for a crash that skips pagehide (process kill): the last
 * <2s of timer drift — no structural progress can be lost.
 */

const SAVE_EVERY_MS = 2000
/** 🎲 v5.62: which slot the player was IN — read at boot so a refresh
 * mid-daily resumes the daily instead of the campaign's draft. */
const ACTIVE_SLOT_KEY = 'elemental-dispatch-active-slot'

/** The fields whose change must persist NOW. Everything not covered
 * here (fight/expire/travel timers, spawn clocks, mission status
 * churn) may lag by one throttle window. Keep this in sync when
 * RunState grows a new kind of player progress.
 *
 * ⚡ v4.89 (perf review #4): the per-mission status letters and
 * returnTrips.length are GONE from the signature — every spawn and
 * status flip was forcing a synchronous full-run stringify exactly on
 * the frames where markers animate in. A dispatch redone after a
 * crash-refresh costs nothing a player would call progress; the 2s
 * heartbeat below covers it anyway. */
function signature(v: RunState | null): string {
  if (!v) return 'null'
  return [
    v.mode ?? '', v.dailyDate ?? '',
    v.phase, v.act, v.hp, v.maxHp, v.gold, v.activeNodeId, v.position,
    v.defeated, v.bossDefeated, v.puzzleLevel ?? '', v.ascension,
    v.roster.length, v.relics.length, v.completedNodeIds.length,
    v.pendingRewards?.length ?? '', v.rewardResolution?.type ?? '',
    v.draft ? 'drafting' : '',
    v.encounter ? `${v.encounter.status}/${v.encounter.successes}` : '',
    // 🎓 v8.02: the practice's inspector closing is a step of its own - a
    // reload right after must not deal the glass lesson again.
    v.encounter?.tutorialInspected ? 'inspected' : '',
    starweaveProgressSignature(v),
  ].join('|')
}

export default defineNuxtPlugin(() => {
  if (ACCOUNT_DELETION_SESSION) return
  const run = useRunState()
  // ☁️ v3.60: the same STRUCTURAL signal that forces a local write is
  // what the cloud wants — real progress, never timer churn. Signed out,
  // every call here is a no-op.
  const sync = useRunSync()

  try {
    // Clean up saves from older, incompatible formats.
    for (let v = 1; v <= 5; v++) localStorage.removeItem(`elemental-dispatch-run-v${v}`)
    // 🎲 v5.62 (per Javi: "when I accidentally refresh during a daily,
    // it takes me back to the draft screen"): boot from the slot that
    // was ACTIVE, not always the campaign. The daily slot only counts
    // while it is TODAY's unfinished run — same rule continueDailyRun
    // enforces; anything else falls back to the campaign.
    let dailySave: Partial<RunState> | null = null
    if (localStorage.getItem(ACTIVE_SLOT_KEY) === 'daily') {
      try {
        const draw = localStorage.getItem(DAILY_STORAGE_KEY)
        const parsed = draw ? (JSON.parse(draw) as Partial<RunState>) : null
        if (parsed && parsed.dailyDate === weeklyDateKey() && !parsed.defeated && !parsed.bossDefeated) {
          dailySave = parsed
        }
      } catch { /* a broken daily save just falls back to the campaign */ }
    }
    if (dailySave) {
      // NO ascension clamp: the daily's A2–9 is seeded, not owned
      // (continueDailyRun does the same).
      run.value = normalizeRun(dailySave)
    } else {
      const raw = localStorage.getItem(RUN_STORAGE_KEY)
      if (raw) {
        // 🏦 v4.92 (audit E8): a save can't claim an ascension the account
        // never unlocked — that laundered 4× payouts and a fake first-clear.
        const asc = useAscension()
        asc.load()
        run.value = normalizeRun(JSON.parse(raw) as Partial<RunState>, asc.unlocked.value)
      }
    }
  } catch (error) {
    console.warn('Could not load saved run, starting fresh.', error)
    localStorage.removeItem(RUN_STORAGE_KEY)
  }

  // 🧷 The churn signal: playtimeMs ticks (1 Hz, SessionClock) exactly
  // on the screens where timers run. While it hasn't moved since the
  // last write, NOTHING writes — which preserves the long-standing
  // contract that an idle title screen never clobbers a localStorage
  // edit (verify.mjs and the test harnesses seed saves there).
  let savedPlaytimeMs = run.value?.playtimeMs ?? -1
  const churned = () => !!run.value && run.value.playtimeMs !== savedPlaytimeMs

  // 🎲 v5.54: DAILY runs persist to their OWN slot, so the campaign
  // save is never touched while the daily is active — and vice versa.
  // Nulling run.value only clears the slot the active run lived in;
  // switching modes just swaps which key the writer targets.
  let lastMode: 'daily' | 'tutorial' | undefined = run.value?.mode
  function keyFor(mode: 'daily' | 'tutorial' | undefined): string {
    return mode === 'daily' ? DAILY_STORAGE_KEY : RUN_STORAGE_KEY
  }
  function write() {
    try {
      const value = run.value
      if (value) {
        localStorage.setItem(keyFor(value.mode), JSON.stringify(value))
        lastMode = value.mode
        localStorage.setItem(ACTIVE_SLOT_KEY, value.mode === 'daily' ? 'daily' : 'campaign')
      } else {
        localStorage.removeItem(keyFor(lastMode))
        // A cleared run leaves the player on the title — the campaign
        // slot is what Continue means from there.
        localStorage.setItem(ACTIVE_SLOT_KEY, 'campaign')
      }
      savedPlaytimeMs = value?.playtimeMs ?? -1
    } catch (error) {
      console.warn('Could not save run.', error)
    }
  }

  // ⚡ v4.89 (perf review #4): the old `{ deep: true }` watcher made Vue
  // re-traverse the ENTIRE run graph (map nodes, roster, missions —
  // thousands of proxy reads plus a thrown-away Set) on every 100ms
  // tick, for the whole fight. Watching the signature GETTER instead
  // tracks only the ~20 fields it reads and fires only when its string
  // actually changes — structural progress still saves instantly.
  watch(
    () => signature(run.value),
    () => {
      write()
      // 🎲 v6.33: write() leaves `lastMode` holding the slot it just
      // wrote OR emptied (and it is seeded from the booted run, so a
      // weekly restored on reload and abandoned before any other
      // checkpoint is still recognised). The sync layer cannot work this
      // out for itself once run.value is null.
      sync.noteProgress(lastMode)
    },
  )

  // 🎓 v7.38 (F39 review): a draft pick is progress too, but it moves
  // neither the signature (the run is 'drafting' either way) nor
  // playtimeMs - a pick made just after a heartbeat and reloaded before
  // the next clock tick was skipped by the pagehide flush. Picks write
  // NOW, locally only: the cloud still hears about the draft at its next
  // structural checkpoint (the sync layer is untouched). Undefined = no
  // draft (or no run): the signature watcher owns those transitions.
  watch(
    () => run.value?.draft?.selected?.join('|'),
    (picks) => { if (picks !== undefined) write() },
  )

  // Timer churn (mission clocks, playtime) lands in one heartbeat write.
  // App-lifetime interval by design; skipped while hidden — pagehide
  // and tab-hide flush below instead.
  setInterval(() => {
    if (churned() && document.visibilityState === 'visible') write()
  }, SAVE_EVERY_MS)

  const flush = () => {
    if (churned()) write()
    // Leaving the page is the last chance to hand the run over.
    sync.flush()
  }
  window.addEventListener('pagehide', flush)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
  // 📱 M6: the app's own trip to the background (the native shell) may come
  // without a visibilitychange, and a backgrounded app can be killed at any
  // moment - the same flush.
  onAppStateChange((active) => { if (!active) flush() })
})
